import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import GestorLayout from '../../components/GestorLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage, formatOnlyDateLisbon } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle 
} from '../../components/ui/dialog';
import { 
  CalendarDays, 
  GripVertical, 
  Clock, 
  AlertTriangle, 
  CheckCircle2, 
  Package, 
  ArrowUp, 
  ArrowDown,
  Filter,
  Search,
  ChevronRight,
  Settings,
  X,
  Save,
  Users,
  List,
  LayoutGrid,
  Play,
  Pause,
  FileText,
  Check,
  Zap,
  Box,
  Layers,
  Wrench,
  ExternalLink,
  Activity,
  Plus,
  Calendar,
  Pencil,
  ClipboardList
} from 'lucide-react';

// Importar BolsaTarefasContent directamente (não lazy load para evitar problemas de renderização)
import { BolsaTarefasContent } from './BolsaTarefas';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRIORIDADE_CONFIG = {
  1: { label: 'Urgente', color: 'bg-red-500', textColor: 'text-red-700', bgLight: 'bg-red-50', border: 'border-red-300' },
  2: { label: 'Alta', color: 'bg-orange-500', textColor: 'text-orange-700', bgLight: 'bg-orange-50', border: 'border-orange-300' },
  3: { label: 'Normal', color: 'bg-blue-500', textColor: 'text-blue-700', bgLight: 'bg-blue-50', border: 'border-blue-300' },
  4: { label: 'Baixa', color: 'bg-stone-400', textColor: 'text-stone-600', bgLight: 'bg-stone-50', border: 'border-stone-300' },
};

// Colunas do Kanban por estado de produção
const COLUNAS_PLANEAMENTO = {
  producao: { label: 'Produção', color: 'bg-green-500', textColor: 'text-green-700', bgLight: 'bg-green-50', icon: Play },
  ready_to_start: { label: 'Ready to Start', color: 'bg-blue-500', textColor: 'text-blue-700', bgLight: 'bg-blue-50', icon: CheckCircle2 },
  aguardar_entrada: { label: 'Aguardar Entrada', color: 'bg-amber-500', textColor: 'text-amber-700', bgLight: 'bg-amber-50', icon: Clock },
};

const STATUS_CONFIG = {
  rascunho: { label: 'Rascunho', color: 'bg-stone-400', icon: FileText },
  pendente: { label: 'Ready to Start', color: 'bg-blue-500', icon: CheckCircle2 },
  aguardar_entrada: { label: 'Aguardar Entrada', color: 'bg-amber-500', icon: Clock },
  em_curso: { label: 'Em Curso', color: 'bg-green-500', icon: Play },
  pausado: { label: 'Pausado', color: 'bg-orange-500', icon: Pause },
  terminado: { label: 'Terminado', color: 'bg-emerald-600', icon: CheckCircle2 },
  cancelado: { label: 'Cancelado', color: 'bg-red-500', icon: X },
};

const FILTER_STORAGE_KEY = 'fabriflow_planeamento_filtros';

export default function Planeamento() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [ofs, setOfs] = useState([]);
  const [loading, setLoading] = useState(true);
  // Tab activa: 'ofs' ou 'pontuais'
  const [activeTab, setActiveTab] = useState(() => {
    return searchParams.get('tab') || 'ofs';
  });
  // Pesquisa persistente - guarda até ser explicitamente limpa
  const [searchTerm, setSearchTerm] = useState(() => {
    return sessionStorage.getItem('planeamento_searchTerm') || '';
  });
  const [filtroStatusList, setFiltroStatusList] = useState(['pendente', 'aguardar_entrada', 'em_curso', 'pausado']);
  const [filtroCliente, setFiltroCliente] = useState('');
  const [filtroAtivo, setFiltroAtivo] = useState(false);
  const [draggedOf, setDraggedOf] = useState(null);
  const [dragOverInfo, setDragOverInfo] = useState(null);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [selectedOfForStatus, setSelectedOfForStatus] = useState(null);
  const [showFilterDialog, setShowFilterDialog] = useState(false);
  const [viewMode, setViewMode] = useState('kanban');
  const [clientes, setClientes] = useState([]);
  const [ordemPorPrioridade, setOrdemPorPrioridade] = useState({});
  const [showArquivados, setShowArquivados] = useState(false);
  const [showEditOfDialog, setShowEditOfDialog] = useState(false);
  const [editingOf, setEditingOf] = useState(null);
  const [editFormData, setEditFormData] = useState({ urgente: false, aguardar_items: [] });
  const [showAcompanharDialog, setShowAcompanharDialog] = useState(false);
  const [acompanharData, setAcompanharData] = useState(null);
  const [acompanharLoading, setAcompanharLoading] = useState(false);
  const [acompanharLoteSelecionado, setAcompanharLoteSelecionado] = useState(null); // Filtro por lote
  const [filtroCategoriaEspecial, setFiltroCategoriaEspecial] = useState(null); // Filtro por categoria especial
  const [categoriasEspeciaisComOfs, setCategoriasEspeciaisComOfs] = useState([]); // Categorias especiais que têm OFs
  const [categoriasMap, setCategoriasMap] = useState({}); // Mapa de categorias por ID

  // Sincronizar tab com URL
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && (tabFromUrl === 'ofs' || tabFromUrl === 'pontuais')) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  // Quando o utilizador chega via deeplink (?of_id=...&lote_id=...), focar a OF assim
  // que os dados estiverem carregados — scroll + filtro de pesquisa pelo número da OF.
  // Usado pela vista Relatórios > Operações ao clicar no OF/Lote de um registo.
  useEffect(() => {
    const ofIdParam = searchParams.get('of_id');
    if (!ofIdParam || !ofs?.length) return;
    const of = ofs.find(o => o.id === ofIdParam);
    if (!of) return;
    setSearchTerm(of.numero_of || '');
    setTimeout(() => {
      const el = document.querySelector(`[data-of-id="${ofIdParam}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-2', 'ring-blue-400', 'ring-offset-2');
        setTimeout(() => el.classList.remove('ring-2', 'ring-blue-400', 'ring-offset-2'), 3000);
      }
    }, 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ofs, searchParams]);

  // Handler para mudar de tab
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setSearchParams({ tab: newTab });
  };

  // Persistir termo de pesquisa no localStorage
  useEffect(() => {
    sessionStorage.setItem('planeamento_searchTerm', searchTerm);
  }, [searchTerm]);

  // Função para limpar pesquisa
  const handleClearSearch = () => {
    setSearchTerm('');
    sessionStorage.removeItem('planeamento_searchTerm');
  };

  // Helper para formatar tempo em H:M:S
  const formatTempoHMS = (horas) => {
    if (!horas || horas <= 0) return '0m';
    const totalMinutos = Math.round(horas * 60);
    const h = Math.floor(totalMinutos / 60);
    const m = totalMinutos % 60;
    if (h > 0) {
      return m > 0 ? `${h}h ${m}m` : `${h}h`;
    }
    return `${m}m`;
  };

  // Navegar para detalhes da OF
  const handleNavigateToOF = (ofId) => {
    navigate(`/gestor/ofs?of=${ofId}`);
  };

  // Abrir painel de acompanhamento
  const handleOpenAcompanhar = async (e, ofId) => {
    e.stopPropagation();
    setAcompanharLoading(true);
    try {
      const response = await axios.get(`${API}/ofs/${ofId}/acompanhamento`);
      setAcompanharData(response.data);
      setShowAcompanharDialog(true);
    } catch (error) {
      console.error('Erro ao carregar acompanhamento:', error);
      toast.error('Erro ao carregar dados de acompanhamento');
    } finally {
      setAcompanharLoading(false);
    }
  };

  // Carregar filtros guardados
  useEffect(() => {
    const savedFilters = sessionStorage.getItem(FILTER_STORAGE_KEY);
    if (savedFilters) {
      try {
        const filters = JSON.parse(savedFilters);
        if (filters.statusList) {
          setFiltroStatusList(filters.statusList);
        }
        setFiltroCliente(filters.cliente || '');
        setFiltroAtivo(true);
      } catch (e) {
        console.error('Erro ao carregar filtros:', e);
      }
    }
  }, []);

  useEffect(() => {
    loadOfs();
  }, []);

  const loadOfs = async () => {
    setLoading(true);
    try {
      // Carregar OFs e categorias em paralelo
      const [ofsResponse, categoriasResponse] = await Promise.all([
        axios.get(`${API}/ofs`),
        axios.get(`${API}/categorias`)
      ]);
      
      const sorted = ofsResponse.data.sort((a, b) => {
        const prioA = a.prioridade || 3;
        const prioB = b.prioridade || 3;
        if (prioA !== prioB) return prioA - prioB;
        const ordemA = a.ordem_planeamento || 999;
        const ordemB = b.ordem_planeamento || 999;
        if (ordemA !== ordemB) return ordemA - ordemB;
        const dataA = a.data_entrega || '9999-12-31';
        const dataB = b.data_entrega || '9999-12-31';
        return dataA.localeCompare(dataB);
      });
      setOfs(sorted);
      
      // Extrair clientes únicos
      const uniqueClientes = [...new Set(ofsResponse.data.map(of => of.cliente).filter(Boolean))];
      setClientes(uniqueClientes);
      
      // Mapa de categorias por ID - guardar no estado
      const catMap = {};
      categoriasResponse.data.forEach(cat => {
        catMap[cat.id] = cat;
      });
      setCategoriasMap(catMap);
      
      // Extrair categorias especiais únicas que têm OFs associadas
      const categoriasEspeciaisUsadas = new Map();
      ofsResponse.data.forEach(of => {
        if (of.categorias_especiais && of.categorias_especiais.length > 0) {
          of.categorias_especiais.forEach(catId => {
            if (!categoriasEspeciaisUsadas.has(catId)) {
              const catInfo = catMap[catId];
              if (catInfo) {
                categoriasEspeciaisUsadas.set(catId, { id: catId, nome: catInfo.nome, codigo: catInfo.codigo });
              }
            }
          });
        }
      });
      setCategoriasEspeciaisComOfs(Array.from(categoriasEspeciaisUsadas.values()));
    } catch (error) {
      toast.error('Erro ao carregar OFs');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const updatePrioridade = async (ofId, novaPrioridade) => {
    try {
      await axios.patch(`${API}/ofs/${ofId}`, { prioridade: novaPrioridade });
      toast.success('Prioridade atualizada');
      loadOfs();
    } catch (error) {
      toast.error('Erro ao atualizar prioridade');
    }
  };

  const updateStatus = async (ofId, novoStatus) => {
    try {
      await axios.patch(`${API}/ofs/${ofId}`, { status: novoStatus });
      toast.success('Estado atualizado');
      setShowStatusDialog(false);
      setSelectedOfForStatus(null);
      loadOfs();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao atualizar estado'));
    }
  };

  // Toggle status no filtro
  const toggleFiltroStatus = (status) => {
    setFiltroStatusList(prev => {
      if (prev.includes(status)) {
        return prev.filter(s => s !== status);
      } else {
        return [...prev, status];
      }
    });
  };

  // Guardar filtros
  const guardarFiltros = () => {
    const filters = {
      statusList: filtroStatusList,
      cliente: filtroCliente
    };
    sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
    setFiltroAtivo(true);
    setShowFilterDialog(false);
    toast.success('Filtros guardados!');
  };

  // Limpar filtros
  const limparFiltros = () => {
    sessionStorage.removeItem(FILTER_STORAGE_KEY);
    setFiltroStatusList(['pendente', 'aguardar_entrada', 'em_curso', 'pausado']);
    setFiltroCliente('');
    setFiltroAtivo(false);
    toast.success('Filtros limpos!');
  };

  // Estados arquivados (Rascunho, Terminado, Cancelado)
  const ESTADOS_ARQUIVADOS = ['rascunho', 'terminado', 'cancelado'];
  
  // Estados activos por coluna
  const ESTADOS_PRODUCAO = ['em_curso', 'pausado'];
  const ESTADOS_READY = ['pendente'];
  const ESTADOS_AGUARDAR = ['aguardar_entrada'];

  // Filtrar OFs - separar activas de arquivadas
  const ofsActivas = ofs.filter(of => !ESTADOS_ARQUIVADOS.includes(of.status));
  const ofsArquivadas = ofs.filter(of => ESTADOS_ARQUIVADOS.includes(of.status));
  
  const ofsFiltradas = (showArquivados ? ofsArquivadas : ofsActivas).filter(of => {
    // Filtro de categoria especial - quando ativo, ignora outros filtros de estado
    if (filtroCategoriaEspecial) {
      const matchCategoriaEspecial = of.categorias_especiais && of.categorias_especiais.includes(filtroCategoriaEspecial);
      const matchSearch = !searchTerm || 
        of.numero_of?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        of.cliente?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        of.artigo?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchCliente = !filtroCliente || of.cliente === filtroCliente;
      return matchCategoriaEspecial && matchSearch && matchCliente;
    }
    
    const matchSearch = !searchTerm || 
      of.numero_of?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      of.cliente?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      of.artigo?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Na vista "Mais OFs" não filtrar por estado
    const matchStatus = filtroStatusList.length > 0 ? filtroStatusList.includes(of.status) : true;
    const matchCliente = !filtroCliente || of.cliente === filtroCliente;
    
    return matchSearch && matchStatus && matchCliente;
  });

  // Agrupar por estado para o Kanban (Produção, Ready to Start, Aguardar Entrada)
  const ofsPorEstado = {
    producao: ofsFiltradas.filter(of => ESTADOS_PRODUCAO.includes(of.status)).sort((a, b) => {
      // Urgentes primeiro, depois por ordem
      if (a.urgente && !b.urgente) return -1;
      if (!a.urgente && b.urgente) return 1;
      return (a.ordem_planeamento || 999) - (b.ordem_planeamento || 999);
    }),
    ready_to_start: ofsFiltradas.filter(of => ESTADOS_READY.includes(of.status)).sort((a, b) => {
      if (a.urgente && !b.urgente) return -1;
      if (!a.urgente && b.urgente) return 1;
      return (a.ordem_planeamento || 999) - (b.ordem_planeamento || 999);
    }),
    aguardar_entrada: ofsFiltradas.filter(of => ESTADOS_AGUARDAR.includes(of.status)).sort((a, b) => {
      if (a.urgente && !b.urgente) return -1;
      if (!a.urgente && b.urgente) return 1;
      return (a.ordem_planeamento || 999) - (b.ordem_planeamento || 999);
    }),
  };

  // Agrupar por prioridade para vista Lista (mantido para compatibilidade)
  const ofsPorPrioridade = {
    1: ofsFiltradas.filter(of => of.prioridade === 1).sort((a, b) => (a.ordem_planeamento || 999) - (b.ordem_planeamento || 999)),
    2: ofsFiltradas.filter(of => of.prioridade === 2).sort((a, b) => (a.ordem_planeamento || 999) - (b.ordem_planeamento || 999)),
    3: ofsFiltradas.filter(of => of.prioridade === 3 || !of.prioridade).sort((a, b) => (a.ordem_planeamento || 999) - (b.ordem_planeamento || 999)),
    4: ofsFiltradas.filter(of => of.prioridade === 4).sort((a, b) => (a.ordem_planeamento || 999) - (b.ordem_planeamento || 999)),
  };

  // Abrir modal de edição de OF
  const handleOpenEditOf = (of) => {
    setEditingOf(of);
    setEditFormData({
      urgente: of.urgente || false,
      aguardar_items: of.aguardar_items || []
    });
    setShowEditOfDialog(true);
  };

  // Guardar edição de OF
  const handleSaveEditOf = async () => {
    if (!editingOf) return;
    try {
      await axios.patch(`${API}/ofs/${editingOf.id}`, {
        urgente: editFormData.urgente,
        aguardar_items: editFormData.aguardar_items
      });
      toast.success('OF actualizada!');
      setShowEditOfDialog(false);
      loadOfs();
    } catch (error) {
      toast.error('Erro ao actualizar OF');
    }
  };

  // Toggle item de aguardar
  const toggleAguardarItem = (tipo) => {
    setEditFormData(prev => {
      const items = prev.aguardar_items || [];
      const existing = items.find(i => i.tipo === tipo);
      if (existing) {
        return {
          ...prev,
          aguardar_items: items.filter(i => i.tipo !== tipo)
        };
      } else {
        return {
          ...prev,
          aguardar_items: [...items, { tipo, recebido: false, data_prevista: '' }]
        };
      }
    });
  };

  // Actualizar item de aguardar
  const updateAguardarItem = (tipo, field, value) => {
    setEditFormData(prev => ({
      ...prev,
      aguardar_items: (prev.aguardar_items || []).map(i => 
        i.tipo === tipo ? { ...i, [field]: value } : i
      )
    }));
  };

  // Drag & Drop handlers - Agora por estado
  const handleDragStart = (e, of, estado, index) => {
    setDraggedOf({ of, sourceEstado: estado, sourceIndex: index });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, estado, index) => {
    e.preventDefault();
    if (draggedOf) {
      setDragOverInfo({ estado, index });
    }
  };

  const handleDragLeave = () => {
    // Manter o dragOverInfo por um momento para evitar flickering
  };

  // Função para atualizar estado da OF
  const updateEstado = async (ofId, novoEstado) => {
    try {
      await axios.patch(`${API}/ofs/${ofId}`, { status: novoEstado });
      toast.success('Estado atualizado');
      loadOfs();
    } catch (error) {
      toast.error('Erro ao atualizar estado');
    }
  };

  // Função para guardar ordem no backend
  const guardarOrdemEstado = async (estado, ofsOrdenadas) => {
    try {
      const ofsParaAtualizar = ofsOrdenadas.map((of, index) => ({
        id: of.id,
        ordem_planeamento: index + 1
      }));
      
      await axios.post(`${API}/ofs/reordenar`, { ofs: ofsParaAtualizar });
    } catch (error) {
      console.error('Erro ao guardar ordem:', error);
    }
  };

  const handleDrop = async (e, targetEstado, targetIndex) => {
    e.preventDefault();
    if (!draggedOf) return;

    const { of: draggedOfData, sourceEstado, sourceIndex } = draggedOf;
    
    // Mapear estado da coluna para o status real
    const statusMap = {
      producao: 'em_curso',
      ready_to_start: 'pendente',
      aguardar_entrada: 'aguardar_entrada'
    };
    
    // Se mudou de estado
    if (sourceEstado !== targetEstado) {
      // Mover para novo estado
      await updateEstado(draggedOfData.id, statusMap[targetEstado]);
      
      // Atualizar ordem no novo estado
      const novaListaEstado = [...ofsPorEstado[targetEstado]];
      novaListaEstado.splice(targetIndex, 0, {...draggedOfData, status: statusMap[targetEstado]});
      await guardarOrdemEstado(targetEstado, novaListaEstado);
      toast.success('OF movida!');
      
      // Pequeno delay para garantir que a BD actualizou
      await new Promise(resolve => setTimeout(resolve, 300));
      loadOfs();
    } else {
      // Reordenação dentro do mesmo estado
      const listaAtual = [...ofsPorEstado[sourceEstado]];
      
      // Remover da posição antiga
      listaAtual.splice(sourceIndex, 1);
      // Inserir na nova posição
      listaAtual.splice(targetIndex, 0, draggedOfData);
      
      // Guardar nova ordem
      await guardarOrdemEstado(sourceEstado, listaAtual);
      toast.success('Ordem guardada!');
      
      // Pequeno delay para garantir que a BD actualizou
      await new Promise(resolve => setTimeout(resolve, 300));
      loadOfs();
    }

    setDraggedOf(null);
    setDragOverInfo(null);
  };

  const handleDragEnd = () => {
    setDraggedOf(null);
    setDragOverInfo(null);
  };

  // Calcular progresso baseado em operações terminadas (usa progresso_real do backend)
  const calcularProgresso = (of) => {
    // O backend já calcula progresso_real baseado em operações concluídas por lote
    if (of.progresso_real !== undefined && of.progresso_real !== null) {
      return Math.round(of.progresso_real);
    }
    
    // Fallback: calcular baseado nas operações com quantidade_produzida >= quantidade
    const operacoes = of.operacoes || [];
    if (operacoes.length === 0) return 0;
    
    let operacoesTerminadas = 0;
    const quantidadeOf = of.quantidade_total || 0;
    
    for (const op of operacoes) {
      const produzidaOp = op.quantidade_produzida || 0;
      // Operação está terminada se produzida >= quantidade da OF
      if (quantidadeOf > 0 && produzidaOp >= quantidadeOf) {
        operacoesTerminadas++;
      }
    }
    
    return Math.round((operacoesTerminadas / operacoes.length) * 100);
  };

  // Contar operações atribuídas
  const contarOperacoesAtribuidas = (of) => {
    const operacoes = of.operacoes || [];
    // Contar operadores únicos atribuídos a todas as operações
    const operadoresUnicos = new Set();
    operacoes.forEach(op => {
      (op.operadores_atribuidos || []).forEach(opId => operadoresUnicos.add(opId));
    });
    return { operadores: operadoresUnicos.size, totalOperacoes: operacoes.length };
  };

  // Renderizar card de OF para Kanban - Agora por estado
  const renderOfCardKanban = (of, estado, index) => {
    const progresso = calcularProgresso(of);
    const statusConfig = STATUS_CONFIG[of.status] || STATUS_CONFIG.pendente;
    const operacoesInfo = contarOperacoesAtribuidas(of);
    const StatusIcon = statusConfig.icon;
    const isDragging = draggedOf?.of.id === of.id;
    const isDropTarget = dragOverInfo?.estado === estado && dragOverInfo?.index === index;
    const isUrgente = of.urgente;
    
    // Itens de aguardar
    const aguardarItems = of.aguardar_items || [];
    const hasAguardarItems = aguardarItems.length > 0;
    const aguardarRecebidos = aguardarItems.filter(item => item.recebido).length;

    return (
      <div
        key={of.id}
        data-of-id={of.id}
        className={`p-3 bg-white rounded-lg cursor-pointer transition-all shadow-sm hover:shadow-md ${
          isDragging ? 'opacity-50 scale-95 border-2 border-primary' : ''
        } ${isDropTarget ? 'ring-2 ring-primary ring-offset-2' : ''} ${
          isUrgente ? 'border-2 border-red-500 bg-red-50/30' : 'border border-stone-200'
        }`}
        draggable
        onClick={() => {
          // Em "Produção" ou "Ready to Start" abre Lotes, caso contrário abre edição
          if (estado === 'producao' || estado === 'ready_to_start' || estado === 'pendente') {
            navigate(`/gestor/ofs?of=${of.id}&tab=lotes`);
          } else {
            handleOpenEditOf(of);
          }
        }}
        onDragStart={(e) => handleDragStart(e, of, estado, index)}
        onDragOver={(e) => handleDragOver(e, estado, index)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, estado, index)}
        onDragEnd={handleDragEnd}
        data-testid={`planeamento-of-${of.numero_of}`}
      >
        <div className="flex items-start gap-2">
          <GripVertical className="h-4 w-4 text-stone-300 flex-shrink-0 mt-1 cursor-grab" />
          
          <div className="flex-1 min-w-0">
            {/* Header */}
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleNavigateToOF(of.id);
                }}
                className="font-bold text-primary text-sm hover:underline flex items-center gap-1"
                title="Abrir detalhes da OF"
              >
                {of.numero_of}
                <ExternalLink className="h-3 w-3 opacity-60" />
              </button>
              {isUrgente && (
                <span className="flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full bg-red-500 text-white">
                  <Zap className="h-3 w-3" />
                  Urgente
                </span>
              )}
              {of.categorias_especiais && of.categorias_especiais.length > 0 && (
                of.categorias_especiais.map(catId => {
                  const cat = categoriasMap[catId];
                  return cat ? (
                    <span key={catId} className="flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full bg-purple-500 text-white" title={`Categoria Especial: ${cat.nome}`}>
                      ⭐ {cat.codigo || cat.nome}
                    </span>
                  ) : null;
                })
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedOfForStatus(of);
                  setShowStatusDialog(true);
                }}
                className={`flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full text-white ${statusConfig.color} hover:opacity-80`}
              >
                <StatusIcon className="h-3 w-3" />
                {statusConfig.label}
              </button>
            </div>
            
            {/* Cliente/Marca e Artigo - Maior e mais visível */}
            <p className="text-base font-semibold text-stone-800 truncate">{of.cliente}</p>
            <p className="text-sm text-stone-600 truncate">{of.artigo}</p>
            
            {/* Datas e Observações */}
            {(of.previsao_entrada || of.data_entrega || of.observacoes) && (
              <div className="mt-1 space-y-0.5">
                {(of.previsao_entrada || of.data_entrega) && (
                  <div className="flex items-center gap-3 text-sm">
                    {of.previsao_entrada && (() => {
                      const d = new Date(of.previsao_entrada);
                      const hoje = new Date(); hoje.setHours(0,0,0,0);
                      const isVencida = d <= hoje;
                      return (
                        <span className="text-stone-500">Prev. Entrada: <span className={`font-semibold ${isVencida ? 'text-red-600 animate-pulse' : 'text-stone-700'}`}>{d.toLocaleDateString('pt-PT')}</span></span>
                      );
                    })()}
                    {of.data_entrega && (
                      <span className="text-stone-500">Entrega: <span className="font-semibold text-stone-700">{new Date(of.data_entrega).toLocaleDateString('pt-PT')}</span></span>
                    )}
                  </div>
                )}
                {of.observacoes && (
                  <p className="px-2 py-1 rounded bg-amber-50 border border-amber-200 text-amber-800 italic text-sm" title={of.observacoes}>{of.observacoes}</p>
                )}
              </div>
            )}
            
            {/* Info Principal - Maior e mais intuitivo */}
            <div className="flex items-center gap-3 mt-2">
              {/* Quantidade */}
              <div className="flex items-center gap-1.5 bg-stone-100 px-2 py-1 rounded-lg" title="Quantidade total">
                <Package className="h-4 w-4 text-stone-600" />
                <span className="text-base font-bold text-stone-800">{of.quantidade_total}</span>
              </div>
              
              {/* Lotes */}
              <div className="flex items-center gap-1.5 bg-blue-50 px-2 py-1 rounded-lg" title="Lotes criados">
                <Layers className="h-4 w-4 text-blue-600" />
                <span className="text-base font-bold text-blue-700">{of.lotes?.length || 0}</span>
              </div>
              
              {/* Operadores/Operações */}
              <div 
                className={`flex items-center gap-1.5 px-2 py-1 rounded-lg ${
                  operacoesInfo.operadores === 0 
                    ? 'bg-red-50' 
                    : operacoesInfo.operadores < operacoesInfo.totalOperacoes 
                      ? 'bg-amber-50' 
                      : 'bg-green-50'
                }`}
                title={`${operacoesInfo.operadores} operador(es) para ${operacoesInfo.totalOperacoes} operações`}
              >
                <Users className={`h-4 w-4 ${
                  operacoesInfo.operadores === 0 
                    ? 'text-red-600' 
                    : operacoesInfo.operadores < operacoesInfo.totalOperacoes 
                      ? 'text-amber-600' 
                      : 'text-green-600'
                }`} />
                <span className={`text-base font-bold ${
                  operacoesInfo.operadores === 0 
                    ? 'text-red-700' 
                    : operacoesInfo.operadores < operacoesInfo.totalOperacoes 
                      ? 'text-amber-700' 
                      : 'text-green-700'
                }`}>
                  {operacoesInfo.operadores}/{operacoesInfo.totalOperacoes}
                </span>
              </div>
            </div>
            
            {/* Data de início (se existir) + Categorias com contagem de lotes */}
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              {of.data_inicio_real && (
                <span className="flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full" title="Data de início">
                  <Play className="h-3 w-3" />
                  Início: {formatOnlyDateLisbon(of.data_inicio_real)}
                </span>
              )}
              {of.categorias_lotes && of.categorias_lotes.length > 0 && (
                of.categorias_lotes.slice(0, 2).map((cat, idx) => {
                  // Converter cor hex para classes Tailwind ou usar estilo inline
                  const bgColor = cat.cor ? `${cat.cor}20` : '#e9d5ff'; // cor com 20% opacidade
                  const textColor = cat.cor || '#7c3aed';
                  return (
                    <span 
                      key={cat.nome || idx} 
                      className="text-xs px-2 py-0.5 rounded-full flex items-center gap-1" 
                      style={{ backgroundColor: bgColor, color: textColor }}
                      title={`${cat.nome}: ${cat.count} lote(s)`}
                    >
                      <span className="truncate max-w-[60px]">{cat.nome}</span>
                      <span 
                        className="px-1 rounded-full font-bold text-[10px]"
                        style={{ backgroundColor: `${cat.cor || '#7c3aed'}40`, color: textColor }}
                      >
                        {cat.count}
                      </span>
                    </span>
                  );
                })
              )}
              {of.categorias_lotes && of.categorias_lotes.length > 2 && (
                <span className="text-xs text-stone-400">+{of.categorias_lotes.length - 2}</span>
              )}
            </div>
            
            {/* Itens de Aguardar - Se estado for aguardar_entrada */}
            {estado === 'aguardar_entrada' && (
              <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                {hasAguardarItems ? (
                  aguardarItems.map((item, idx) => (
                    <span
                      key={idx}
                      className={`text-sm px-2 py-1 rounded-full flex items-center gap-1.5 font-medium ${
                        item.recebido 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-amber-100 text-amber-700'
                      }`}
                      title={item.data_prevista ? `Previsto: ${formatOnlyDateLisbon(item.data_prevista)}` : ''}
                    >
                      {item.tipo === 'estrutura' && <Layers className="h-4 w-4" />}
                      {item.tipo === 'pecas' && <Box className="h-4 w-4" />}
                      {item.tipo === 'acessorios' && <Wrench className="h-4 w-4" />}
                      {item.tipo.charAt(0).toUpperCase() + item.tipo.slice(1)}
                      {item.recebido && <Check className="h-4 w-4" />}
                      {!item.recebido && item.data_prevista && (
                        <span className="text-xs opacity-75">({formatOnlyDateLisbon(item.data_prevista)})</span>
                      )}
                    </span>
                  ))
                ) : (
                  <span className="text-sm px-2 py-1 rounded-full bg-stone-100 text-stone-500 flex items-center gap-1.5 font-medium">
                    <Clock className="h-4 w-4" />
                    Clique para definir itens
                  </span>
                )}
              </div>
            )}
            
            {/* Ações */}
            <div className="mt-3">
              <div className="flex justify-end">
                <div className="flex items-center gap-3">
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate(`/gestor/ofs?of=${of.id}&tab=lotes`); }}
                    className="flex items-center gap-1.5 text-stone-600 hover:text-stone-800 font-medium text-sm px-3 py-1.5 bg-stone-100 hover:bg-stone-200 rounded-lg transition-colors"
                    title="Gerir Lotes"
                  >
                    <Package className="h-4 w-4" />
                    Lotes
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate(`/gestor/ofs?of=${of.id}&tab=operacoes`); }}
                    className="flex items-center gap-1.5 text-stone-600 hover:text-stone-800 font-medium text-sm px-3 py-1.5 bg-stone-100 hover:bg-stone-200 rounded-lg transition-colors"
                    title="Gerir Operações"
                  >
                    <Settings className="h-4 w-4" />
                    Operações
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate(`/gestor/ofs?of=${of.id}&tab=acompanhar`); }}
                    className="flex items-center gap-1.5 text-primary hover:text-primary/80 font-semibold text-sm px-3 py-1.5 bg-primary/10 hover:bg-primary/20 rounded-lg transition-colors"
                    title="Acompanhar OF"
                  >
                    <Activity className="h-4 w-4" />
                    Acompanhar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Renderizar card de OF para Lista
  const renderOfCardList = (of, index) => {
    const progresso = calcularProgresso(of);
    const statusConfig = STATUS_CONFIG[of.status] || STATUS_CONFIG.pendente;
    const operacoesInfo = contarOperacoesAtribuidas(of);
    const config = PRIORIDADE_CONFIG[of.prioridade || 3];
    const StatusIcon = statusConfig.icon;

    return (
      <Card
        key={of.id}
        className={`p-4 cursor-pointer transition-all hover:shadow-md border-l-4 ${config.border}`}
        data-testid={`planeamento-of-${of.numero_of}`}
      >
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            {/* Header */}
            <div className="flex items-center gap-2 mb-2">
              <span className="font-bold text-primary text-lg">{of.numero_of}</span>
              {of.urgente && (
                <span className="flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full bg-red-500 text-white">
                  <Zap className="h-3 w-3" />
                  Urgente
                </span>
              )}
              {of.categorias_especiais && of.categorias_especiais.length > 0 && (
                of.categorias_especiais.map(catId => {
                  const cat = categoriasMap[catId];
                  return cat ? (
                    <span key={catId} className="flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full bg-purple-500 text-white" title={`Categoria Especial: ${cat.nome}`}>
                      ⭐ {cat.codigo || cat.nome}
                    </span>
                  ) : null;
                })
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedOfForStatus(of);
                  setShowStatusDialog(true);
                }}
                className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full text-white ${statusConfig.color} hover:opacity-80`}
              >
                <StatusIcon className="h-3 w-3" />
                {statusConfig.label}
              </button>
              <span className={`text-xs px-2 py-0.5 rounded-full ${config.bgLight} ${config.textColor}`}>
                {config.label}
              </span>
            </div>
            
            {/* Cliente e Artigo */}
            <p className="text-base font-semibold text-stone-800">{of.cliente}</p>
            <p className="text-sm text-stone-600">{of.artigo}</p>

            {/* Datas e Observações */}
            {(of.previsao_entrada || of.data_entrega || of.observacoes) && (
              <div className="mt-1 text-xs space-y-0.5">
                {of.previsao_entrada && (
                  <p className="text-stone-500">Prev. Entrada: <span className="font-medium text-stone-700">{formatOnlyDateLisbon(of.previsao_entrada)}</span></p>
                )}
                {of.data_entrega && (
                  <p className="text-stone-500">Data Entrega: <span className="font-medium text-stone-700">{formatOnlyDateLisbon(of.data_entrega)}</span></p>
                )}
                {of.observacoes && (
                  <p className="text-stone-400 italic truncate" title={of.observacoes}>{of.observacoes}</p>
                )}
              </div>
            )}
            
            {/* Info Row */}
            <div className="flex items-center gap-4 mt-3 text-xs text-stone-500">
              <div className="flex items-center gap-1">
                <Package className="h-3.5 w-3.5" />
                <span className="font-medium">{of.quantidade_total}</span>
              </div>
              {of.data_entrega && (
                <div className="flex items-center gap-1">
                  <CalendarDays className="h-3.5 w-3.5" />
                  <span>{formatOnlyDateLisbon(of.data_entrega)}</span>
                </div>
              )}
              <div className={`flex items-center gap-1 ${
                operacoesInfo.operadores === 0 
                  ? 'text-red-600' 
                  : operacoesInfo.operadores < operacoesInfo.totalOperacoes 
                    ? 'text-amber-600' 
                    : 'text-green-600'
              }`}>
                <Users className="h-3.5 w-3.5" />
                <span className="font-medium">{operacoesInfo.operadores}/{operacoesInfo.totalOperacoes}</span>
              </div>
            </div>
            
            {/* Barra de progresso */}
            <div className="mt-3">
              <div className="flex items-center justify-between text-xs text-stone-500 mb-1">
                <span>Progresso</span>
                <span className="font-bold">{progresso}%</span>
              </div>
              <div className="w-full bg-stone-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all ${
                    progresso === 100 ? 'bg-green-500' : 
                    progresso > 50 ? 'bg-blue-500' : 
                    progresso > 0 ? 'bg-amber-500' : 'bg-stone-300'
                  }`}
                  style={{ width: `${progresso}%` }}
                />
              </div>
            </div>
          </div>
          
          {/* Ações de prioridade */}
          <div className="flex flex-col gap-1">
            {(of.prioridade || 3) > 1 && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={(e) => { e.stopPropagation(); updatePrioridade(of.id, (of.prioridade || 3) - 1); }}
                title="Aumentar prioridade"
              >
                <ArrowUp className="h-4 w-4" />
              </Button>
            )}
            {(of.prioridade || 3) < 4 && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={(e) => { e.stopPropagation(); updatePrioridade(of.id, (of.prioridade || 3) + 1); }}
                title="Diminuir prioridade"
              >
                <ArrowDown className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </Card>
    );
  };

  // Contar OFs por status selecionado
  const countByStatus = () => {
    return filtroStatusList.reduce((acc, status) => {
      return acc + ofs.filter(of => of.status === status).length;
    }, 0);
  };

  return (
    <GestorLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold text-stone-900 mb-2">Planeamento</h1>
            <p className="text-stone-600">Gerir produção, OFs e tarefas pontuais</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Botão Nova OF - só aparece na tab de OFs */}
            {activeTab === 'ofs' && (
              <Button
                onClick={() => navigate('/gestor/ofs?criar=true')}
                className="rounded-full"
                data-testid="btn-nova-of-planeamento"
              >
                <Plus className="h-4 w-4 mr-2" />
                Nova OF
              </Button>
            )}
          </div>
        </div>

        {/* Tabs para alternar entre OFs e Tarefas Pontuais */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2 mb-4 bg-stone-800 p-1 rounded-lg">
            <TabsTrigger value="ofs" className="flex items-center gap-2 text-white data-[state=active]:bg-white data-[state=active]:text-stone-900 data-[state=active]:font-bold rounded-md py-2" data-testid="tab-ofs">
              <Package className="h-4 w-4" />
              Ordens de Fabrico
            </TabsTrigger>
            <TabsTrigger value="pontuais" className="flex items-center gap-2 text-white data-[state=active]:bg-white data-[state=active]:text-stone-900 data-[state=active]:font-bold rounded-md py-2" data-testid="tab-pontuais">
              <ClipboardList className="h-4 w-4" />
              Tarefas Pontuais
            </TabsTrigger>
          </TabsList>

          {/* Conteúdo da Tab OFs */}
          <TabsContent value="ofs" className="mt-0">
            {/* Filtro de Categorias Especiais - Linha de botões */}
            {categoriasEspeciaisComOfs.length > 0 && (
              <div className="flex flex-wrap gap-2 items-center mb-4">
                <span className="text-sm text-stone-500 mr-2">Categorias Especiais:</span>
                <Button
                  variant={filtroCategoriaEspecial === null ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFiltroCategoriaEspecial(null)}
                  className="rounded-full text-xs h-7"
                >
                  Todas
                </Button>
                {categoriasEspeciaisComOfs.map(cat => (
                  <Button
                    key={cat.id}
                    variant={filtroCategoriaEspecial === cat.id ? "default" : "outline"}
                    size="sm"
                    onClick={() => setFiltroCategoriaEspecial(cat.id)}
                    className={`rounded-full text-xs h-7 ${filtroCategoriaEspecial === cat.id ? 'bg-purple-600 hover:bg-purple-700' : 'border-purple-300 text-purple-700 hover:bg-purple-50'}`}
                  >
                    {cat.codigo || cat.nome}
                  </Button>
                ))}
              </div>
            )}

        {/* Filtros */}
        <Card className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            {/* Pesquisa */}
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
              <Input
                placeholder="Pesquisar OF, marca ou artigo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 pr-9"
              />
              {searchTerm && (
                <button
                  onClick={handleClearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 transition-colors"
                  title="Limpar pesquisa"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            
            {/* Indicador de pesquisa ativa */}
            {searchTerm && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm">
                <span>Pesquisa: <strong>"{searchTerm}"</strong></span>
                <button onClick={handleClearSearch} className="hover:bg-primary/20 rounded-full p-0.5">
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
            
            {/* Botão de Filtros de Estado */}
            <Button
              variant="outline"
              onClick={() => setShowFilterDialog(true)}
              className="rounded-full flex items-center gap-2"
            >
              <Filter className="h-4 w-4" />
              Estados ({filtroStatusList.length})
              {filtroAtivo && <span className="w-2 h-2 rounded-full bg-primary"></span>}
            </Button>

            {/* Filtro de Cliente */}
            <select
              value={filtroCliente}
              onChange={(e) => setFiltroCliente(e.target.value)}
              className="px-3 py-2 border border-stone-300 rounded-full text-sm focus:ring-2 focus:ring-primary"
            >
              <option value="">Todas as marcas</option>
              {clientes.map(cliente => (
                <option key={cliente} value={cliente}>{cliente}</option>
              ))}
            </select>

            {/* Botão de limpar filtros */}
            {filtroAtivo && (
              <Button
                variant="ghost"
                size="sm"
                onClick={limparFiltros}
                className="rounded-full text-red-600 hover:bg-red-50"
                title="Limpar filtros guardados"
              >
                <X className="h-4 w-4 mr-1" />
                Limpar
              </Button>
            )}

            {/* Botão Mais OFs (Arquivados) */}
            <Button
              variant={showArquivados ? "default" : "outline"}
              onClick={() => { setShowArquivados(!showArquivados); setFiltroCategoriaEspecial(null); }}
              className="rounded-full flex items-center gap-2"
            >
              <FileText className="h-4 w-4" />
              {showArquivados ? 'Ver Activas' : 'Mais OFs'}
              <span className="bg-stone-200 text-stone-700 px-2 py-0.5 rounded-full text-xs">
                {showArquivados ? ofsActivas.length : ofsArquivadas.length}
              </span>
            </Button>

            {/* Botão atalho Rascunhos */}
            {!showArquivados && (
              <Button
                variant="outline"
                onClick={() => { setShowArquivados(true); setFiltroCategoriaEspecial(null); setFiltroStatusList(['rascunho']); setFiltroAtivo(true); }}
                className="rounded-full flex items-center gap-2 border-stone-300 text-stone-600 hover:bg-stone-100"
                data-testid="btn-rascunhos"
              >
                <FileText className="h-4 w-4" />
                Rascunhos
                <span className="bg-stone-200 text-stone-600 px-2 py-0.5 rounded-full text-xs">
                  {ofsArquivadas.filter(of => of.status === 'rascunho').length}
                </span>
              </Button>
            )}

            {/* Quando em vista "Mais OFs": atalhos para filtrar por Rascunho/Terminado/Cancelado */}
            {showArquivados && (
              <>
                {[
                  { key: 'rascunho', label: 'Rascunhos', colorActive: 'bg-stone-600 text-white border-stone-600', colorIdle: 'border-stone-300 text-stone-600 hover:bg-stone-100' },
                  { key: 'terminado', label: 'Terminadas', colorActive: 'bg-emerald-600 text-white border-emerald-600', colorIdle: 'border-emerald-300 text-emerald-700 hover:bg-emerald-50' },
                  { key: 'cancelado', label: 'Canceladas', colorActive: 'bg-red-600 text-white border-red-600', colorIdle: 'border-red-300 text-red-700 hover:bg-red-50' },
                ].map(({ key, label, colorActive, colorIdle }) => {
                  const active = filtroStatusList.length === 1 && filtroStatusList[0] === key;
                  const count = ofsArquivadas.filter(of => of.status === key).length;
                  return (
                    <Button
                      key={key}
                      variant="outline"
                      onClick={() => {
                        if (active) { setFiltroStatusList([]); setFiltroAtivo(false); }
                        else { setFiltroStatusList([key]); setFiltroAtivo(true); }
                      }}
                      className={`rounded-full flex items-center gap-2 ${active ? colorActive : colorIdle}`}
                      data-testid={`btn-arquivado-${key}`}
                    >
                      <FileText className="h-4 w-4" />
                      {label}
                      <span className={`${active ? 'bg-white/25 text-white' : 'bg-stone-200 text-stone-600'} px-2 py-0.5 rounded-full text-xs`}>{count}</span>
                    </Button>
                  );
                })}
              </>
            )}

            <span className="text-sm text-stone-500">
              {ofsFiltradas.length} OFs {showArquivados ? '(rascunhos/terminadas/canceladas)' : 'activas'}
            </span>
          </div>
        </Card>

        {/* Vista Kanban (Grid) - Por Estado: Produção, Ready to Start, Aguardar Entrada */}
        {!showArquivados && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {['producao', 'ready_to_start', 'aguardar_entrada'].map(estado => {
              const config = COLUNAS_PLANEAMENTO[estado];
              const ofsNoEstado = ofsPorEstado[estado];
              const isDropZone = dragOverInfo?.estado === estado;
              const ColIcon = config.icon;
              
              return (
                <div key={estado} className="space-y-3">
                  {/* Cabeçalho da coluna */}
                  <div className={`p-3 rounded-lg ${config.bgLight}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <ColIcon className={`h-5 w-5 ${config.textColor}`} />
                        <span className={`font-semibold ${config.textColor}`}>{config.label}</span>
                      </div>
                      <span className={`text-sm font-bold ${config.textColor} bg-white/50 px-2 py-0.5 rounded-full`}>
                        {ofsNoEstado.length}
                      </span>
                    </div>
                  </div>

                  {/* Lista de OFs com drop zone */}
                  <div 
                    className={`space-y-3 min-h-[300px] p-2 rounded-lg transition-colors ${
                      isDropZone ? 'bg-primary/10 border-2 border-dashed border-primary' : 'bg-stone-50'
                    }`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      if (ofsNoEstado.length === 0) {
                        setDragOverInfo({ estado, index: 0 });
                      }
                    }}
                    onDrop={(e) => {
                      if (ofsNoEstado.length === 0) {
                        handleDrop(e, estado, 0);
                      }
                    }}
                  >
                    {ofsNoEstado.length === 0 ? (
                      <div className={`p-4 border-2 border-dashed rounded-lg text-center ${config.bgLight} border-stone-200`}>
                        <ColIcon className="h-8 w-8 mx-auto text-stone-300 mb-2" />
                        <p className="text-xs text-stone-400">Arraste OFs para aqui</p>
                      </div>
                    ) : (
                      ofsNoEstado.map((of, index) => renderOfCardKanban(of, estado, index))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Vista Arquivados (Lista simples) */}
        {showArquivados && (
          <div className="space-y-3">
            {ofsFiltradas.length === 0 ? (
              <Card className="p-8 text-center">
                <FileText className="h-12 w-12 mx-auto text-stone-300 mb-3" />
                <p className="text-stone-500">Nenhuma OF arquivada encontrada</p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {ofsFiltradas.map((of) => {
                  const statusConfig = STATUS_CONFIG[of.status] || STATUS_CONFIG.rascunho;
                  const StatusIcon = statusConfig.icon;
                  const progresso = calcularProgresso(of);
                  const totalOperacoes = of.operacoes?.length || 0;
                  const totalLotes = of.lotes?.length || 0;
                  
                  return (
                    <Card key={of.id} className="p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <span className="font-bold text-primary">{of.numero_of}</span>
                          <p className="text-sm text-stone-600">{of.cliente}</p>
                        </div>
                        <button
                          onClick={() => {
                            setSelectedOfForStatus(of);
                            setShowStatusDialog(true);
                          }}
                          className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full text-white ${statusConfig.color} hover:opacity-80 transition-opacity`}
                          title="Clique para alterar estado"
                        >
                          <StatusIcon className="h-3 w-3" />
                          {statusConfig.label}
                        </button>
                      </div>
                      <p className="text-xs text-stone-500 mb-2">{of.artigo}</p>
                      
                      {/* Progresso e estatísticas */}
                      <div className="mb-3">
                        <div className="flex items-center justify-between text-xs text-stone-500 mb-1">
                          <span>{progresso}% concluído</span>
                          <span>{of.quantidade_produzida || 0} / {of.quantidade_total} un.</span>
                        </div>
                        <div className="w-full bg-stone-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full transition-all ${
                              of.status === 'terminado' ? 'bg-emerald-500' : 'bg-primary'
                            }`}
                            style={{ width: `${Math.min(progresso, 100)}%` }}
                          />
                        </div>
                      </div>
                      
                      {/* Info compacta */}
                      <div className="flex items-center gap-4 text-xs text-stone-500 mb-3">
                        <span className="flex items-center gap-1">
                          <Layers className="h-3 w-3" />
                          {totalOperacoes} ops
                        </span>
                        <span className="flex items-center gap-1">
                          <Package className="h-3 w-3" />
                          {totalLotes} lotes
                        </span>
                        {of.previsao_entrada && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Ent: {formatOnlyDateLisbon(of.previsao_entrada)}
                          </span>
                        )}
                        {of.data_entrega && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Entr: {formatOnlyDateLisbon(of.data_entrega)}
                          </span>
                        )}
                      </div>
                      {of.observacoes && (
                        <p className="text-xs text-stone-400 italic truncate mb-3" title={of.observacoes}>{of.observacoes}</p>
                      )}
                      
                      {/* Botões de ação - consultar mesmo terminadas */}
                      <div className="flex items-center gap-2 pt-2 border-t border-stone-100">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 h-9 text-sm"
                          onClick={() => navigate(`/gestor/ofs?of=${of.id}&tab=acompanhar`)}
                          title="Acompanhar"
                        >
                          <Activity className="h-4 w-4 mr-1.5" />
                          Consultar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-9 text-sm px-3"
                          onClick={() => navigate(`/gestor/ofs?of=${of.id}&tab=lotes`)}
                          title="Ver Lotes"
                        >
                          <Package className="h-4 w-4 mr-1" />
                          Lotes
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-9 text-sm px-3"
                          onClick={() => navigate(`/gestor/ofs?of=${of.id}&tab=operacoes`)}
                          title="Ver Operações"
                        >
                          <Layers className="h-4 w-4 mr-1" />
                          Operações
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-9 text-sm px-3"
                          onClick={() => navigate(`/gestor/ofs?of=${of.id}&tab=editar`)}
                          title="Editar OF"
                          data-testid={`edit-of-${of.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {of.status === 'rascunho' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 text-xs"
                            onClick={() => updateStatus(of.id, 'aguardar_entrada')}
                          >
                            Iniciar
                          </Button>
                        )}
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Legenda */}
        <Card className="p-4">
          <h3 className="font-semibold text-stone-800 mb-3">Legenda</h3>
          <div className="flex flex-wrap gap-6">
            {Object.entries(COLUNAS_PLANEAMENTO).map(([estado, config]) => {
              const Icon = config.icon;
              return (
                <div key={estado} className="flex items-center gap-2">
                  <Icon className={`h-4 w-4 ${config.textColor}`} />
                  <span className="text-sm text-stone-600">{config.label}</span>
                </div>
              );
            })}
            <div className="border-l border-stone-200 pl-4 ml-2 flex items-center gap-2">
              <Zap className="h-4 w-4 text-red-500" />
              <span className="text-sm text-stone-600">OF Urgente</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-stone-500" />
              <span className="text-sm text-stone-600">Operações atribuídas</span>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-4 text-xs text-stone-500">
            <span className="flex items-center gap-1">
              <Layers className="h-3 w-3" /> Estrutura
            </span>
            <span className="flex items-center gap-1">
              <Box className="h-3 w-3" /> Peças
            </span>
            <span className="flex items-center gap-1">
              <Wrench className="h-3 w-3" /> Acessórios
            </span>
          </div>
          <p className="text-xs text-stone-500 mt-3">
            Arraste os cards entre colunas para alterar o estado. Clique no estado para alterar manualmente.
          </p>
        </Card>
          </TabsContent>

          {/* Conteúdo da Tab Tarefas Pontuais */}
          <TabsContent value="pontuais" className="mt-0">
            <BolsaTarefasContent />
          </TabsContent>
        </Tabs>
      </div>

      {/* Dialog de Filtros de Estado */}
      <Dialog open={showFilterDialog} onOpenChange={setShowFilterDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Filtrar por Estado</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-stone-600">
              Selecione os estados que deseja ver na lista:
            </p>
            
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(STATUS_CONFIG).map(([status, config]) => {
                const Icon = config.icon;
                const isSelected = filtroStatusList.includes(status);
                return (
                  <button
                    key={status}
                    onClick={() => toggleFiltroStatus(status)}
                    className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all ${
                      isSelected 
                        ? 'border-primary bg-primary/5' 
                        : 'border-stone-200 hover:border-stone-300'
                    }`}
                  >
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                      isSelected ? 'bg-primary text-white' : 'bg-stone-100'
                    }`}>
                      {isSelected ? <Check className="h-4 w-4" /> : <Icon className="h-3 w-3 text-stone-400" />}
                    </div>
                    <span className={`text-sm font-medium ${isSelected ? 'text-primary' : 'text-stone-600'}`}>
                      {config.label}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="pt-4 border-t flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setFiltroStatusList(Object.keys(STATUS_CONFIG));
                }}
              >
                Selecionar Todos
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setFiltroStatusList([])}
              >
                Limpar
              </Button>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowFilterDialog(false)}
              >
                Cancelar
              </Button>
              <Button
                className="flex-1"
                onClick={guardarFiltros}
              >
                <Save className="h-4 w-4 mr-2" />
                Guardar Filtros
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de Alteração de Estado */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Alterar Estado - {selectedOfForStatus?.numero_of}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3">
            <p className="text-sm text-stone-600">
              Estado atual: <span className="font-medium">{STATUS_CONFIG[selectedOfForStatus?.status]?.label}</span>
            </p>
            
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(STATUS_CONFIG).map(([status, config]) => {
                const Icon = config.icon;
                const isCurrentStatus = selectedOfForStatus?.status === status;
                return (
                  <Button
                    key={status}
                    variant={isCurrentStatus ? 'default' : 'outline'}
                    className={`justify-start ${isCurrentStatus ? '' : 'hover:bg-stone-50'}`}
                    onClick={() => !isCurrentStatus && updateStatus(selectedOfForStatus.id, status)}
                    disabled={isCurrentStatus}
                  >
                    <Icon className="h-4 w-4 mr-2" />
                    {config.label}
                  </Button>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de Edição de OF (Urgente e Itens de Aguardar) */}
      <Dialog open={showEditOfDialog} onOpenChange={setShowEditOfDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Editar OF - {editingOf?.numero_of}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Info da OF */}
            <div className="bg-stone-50 p-3 rounded-lg">
              <p className="font-medium text-stone-700">{editingOf?.cliente}</p>
              <p className="text-sm text-stone-500">{editingOf?.artigo}</p>
              <p className="text-xs text-stone-400 mt-1">Quantidade: {editingOf?.quantidade_total} un.</p>
            </div>

            {/* Campo Urgente */}
            <div className="flex items-center gap-3 p-3 rounded-lg border border-stone-200">
              <input
                type="checkbox"
                id="edit-urgente"
                checked={editFormData.urgente}
                onChange={(e) => setEditFormData({...editFormData, urgente: e.target.checked})}
                className="h-5 w-5 rounded border-stone-300 text-red-600 focus:ring-red-500"
              />
              <label htmlFor="edit-urgente" className="flex items-center gap-2 cursor-pointer">
                <Zap className="h-5 w-5 text-red-500" />
                <span className="font-medium text-stone-700">Marcar como Urgente</span>
              </label>
            </div>

            {/* Itens de Aguardar */}
            <div className="p-4 rounded-lg border border-amber-200 bg-amber-50">
              <p className="font-medium text-stone-700 mb-3 flex items-center gap-2">
                <Clock className="h-5 w-5 text-amber-600" />
                Itens em falta (A Aguardar)
              </p>
              <div className="space-y-3">
                {[
                  { tipo: 'estrutura', label: 'Estrutura', icon: Layers },
                  { tipo: 'pecas', label: 'Peças', icon: Box },
                  { tipo: 'acessorios', label: 'Acessórios', icon: Wrench }
                ].map(({ tipo, label, icon: Icon }) => {
                  const item = editFormData.aguardar_items?.find(i => i.tipo === tipo) || null;
                  const isChecked = !!item;
                  
                  return (
                    <div key={tipo} className={`p-3 rounded-lg border ${isChecked ? 'border-amber-300 bg-amber-50' : 'border-stone-200 bg-white'}`}>
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          id={`modal-aguardar-${tipo}`}
                          checked={isChecked}
                          onChange={() => toggleAguardarItem(tipo)}
                          className="h-5 w-5 rounded border-stone-300 text-amber-600 focus:ring-amber-500"
                        />
                        <Icon className="h-5 w-5 text-amber-600" />
                        <label htmlFor={`modal-aguardar-${tipo}`} className="text-sm font-medium text-stone-700 flex-1">{label}</label>
                      </div>
                      {isChecked && (
                        <div className="mt-3 ml-8 space-y-2">
                          <div className="flex items-center gap-2">
                            <label className="text-xs text-stone-500 w-24">Data prevista:</label>
                            <Input
                              type="date"
                              value={item?.data_prevista || ''}
                              onChange={(e) => updateAguardarItem(tipo, 'data_prevista', e.target.value)}
                              className="flex-1 h-8 text-sm"
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              id={`modal-recebido-${tipo}`}
                              checked={item?.recebido || false}
                              onChange={(e) => updateAguardarItem(tipo, 'recebido', e.target.checked)}
                              className="h-4 w-4 rounded border-stone-300 text-green-600 focus:ring-green-500"
                            />
                            <label htmlFor={`modal-recebido-${tipo}`} className="text-sm text-green-600 font-medium cursor-pointer">
                              ✓ Já recebido
                            </label>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Botões */}
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowEditOfDialog(false)}
              >
                Cancelar
              </Button>
              <Button
                className="flex-1"
                onClick={handleSaveEditOf}
              >
                <Save className="h-4 w-4 mr-2" />
                Guardar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Acompanhamento da OF */}
      <Dialog open={showAcompanharDialog} onOpenChange={(open) => { 
        setShowAcompanharDialog(open); 
        if (!open) { 
          setAcompanharData(null);
          setAcompanharLoteSelecionado(null);
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-3">
              <Activity className="h-6 w-6 text-primary" />
              Acompanhamento - {acompanharData?.of?.numero_of}
            </DialogTitle>
          </DialogHeader>
          
          {acompanharLoading && !acompanharData && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
            </div>
          )}
          
          {acompanharData && (
            <div className="space-y-6">
              {/* Header com Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="p-4 bg-primary/5">
                  <p className="text-sm text-stone-500">Marca</p>
                  <p className="text-lg font-bold text-stone-900">{acompanharData.of?.cliente}</p>
                </Card>
                <Card className="p-4 bg-primary/5">
                  <p className="text-sm text-stone-500">Artigo</p>
                  <p className="text-lg font-bold text-stone-900">{acompanharData.of?.artigo}</p>
                </Card>
                <Card className="p-4 bg-primary/5">
                  <p className="text-sm text-stone-500">Quantidade</p>
                  <p className="text-lg font-bold text-stone-900">{acompanharData.of?.quantidade_total} un.</p>
                </Card>
                <Card className="p-4 bg-green-50">
                  <p className="text-sm text-green-600">Concluído</p>
                  <p className="text-lg font-bold text-green-700">{acompanharData.resumo?.percentagem_concluida}%</p>
                </Card>
              </div>
              
              {/* Resumo de Anomalias - Sempre mostrar */}
              <div className="flex gap-4">
                <Card className="p-3 bg-red-50 border-red-200 flex-1">
                  <p className="text-xs text-red-600">Defeitos</p>
                  <p className="text-xl font-bold text-red-700">{acompanharData.resumo?.defeitos || 0}</p>
                </Card>
                <Card className="p-3 bg-amber-50 border-amber-200 flex-1">
                  <p className="text-xs text-amber-600">Arranjos</p>
                  <p className="text-xl font-bold text-amber-700">{acompanharData.resumo?.arranjos || 0}</p>
                </Card>
              </div>
              
              {/* Progresso Geral */}
              <div className="p-4 bg-stone-50 rounded-lg">
                <div className="flex justify-between text-sm mb-2">
                  <span className="font-medium">Progresso</span>
                  <span className="font-bold text-primary">{acompanharData.of?.quantidade_produzida || 0} / {acompanharData.of?.quantidade_total}</span>
                </div>
                <div className="w-full h-3 bg-stone-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-primary rounded-full"
                    style={{ width: `${acompanharData.resumo?.percentagem_concluida || 0}%` }}
                  />
                </div>
              </div>

              {/* Filtro por Lote */}
              {acompanharData.lotes && acompanharData.lotes.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-3">Lotes ({acompanharData.lotes.length})</h3>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setAcompanharLoteSelecionado(null)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        !acompanharLoteSelecionado 
                          ? 'bg-primary text-white' 
                          : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                      }`}
                    >
                      Todos os Lotes
                    </button>
                    {acompanharData.lotes.filter(l => l.status === 'aprovado').map((lote) => (
                      <button
                        key={lote.id}
                        onClick={() => setAcompanharLoteSelecionado(lote.id)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          acompanharLoteSelecionado === lote.id 
                            ? 'bg-primary text-white' 
                            : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                        }`}
                      >
                        {lote.nome_lote || lote.codigo_lote || `Lote #${lote.numero_lote}`}
                        <span className="ml-1 opacity-70">({lote.quantidade_ajustada || lote.quantidade} un.)</span>
                      </button>
                    ))}
                  </div>
                  
                  {/* Detalhes do lote selecionado */}
                  {acompanharLoteSelecionado && (() => {
                    const lote = acompanharData.lotes.find(l => l.id === acompanharLoteSelecionado);
                    if (!lote) return null;
                    return (
                      <Card className="p-4 mt-3 bg-blue-50 border-blue-200">
                        <div className="flex justify-between items-center mb-3">
                          <div>
                            <p className="font-bold text-blue-900">{lote.nome_lote || lote.codigo_lote}</p>
                            <p className="text-sm text-blue-700">{lote.categoria_nome || 'Sem Categoria'}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-blue-700">{lote.percentagem || 0}%</p>
                            <p className="text-xs text-blue-600">{lote.quantidade_produzida || 0}/{lote.quantidade_ajustada || lote.quantidade} un.</p>
                          </div>
                        </div>
                        <div className="flex gap-3 text-sm">
                          <span className="text-red-600">Defeitos: <strong>{lote.defeitos || 0}</strong></span>
                          <span className="text-amber-600">Arranjos: <strong>{lote.arranjos || 0}</strong></span>
                          <span className="text-stone-600">Tempo: <strong>{formatTempoHMS(lote.tempo_total_horas)}</strong></span>
                        </div>
                      </Card>
                    );
                  })()}
                </div>
              )}

              {/* Operações */}
              <div>
                <h3 className="font-semibold mb-3">
                  Operações ({acompanharData.operacoes?.length || 0})
                  {acompanharLoteSelecionado && <span className="text-sm font-normal text-stone-500 ml-2">(filtradas por lote)</span>}
                </h3>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {acompanharData.operacoes?.map((op, idx) => (
                    <Card key={op.operacao_id || idx} className="p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs bg-stone-100 px-2 py-0.5 rounded">{op.codigo}</span>
                          <span className="font-medium text-sm">{op.nome}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-lg font-bold text-primary">{op.percentagem}%</span>
                        </div>
                      </div>
                      <div className="w-full h-1.5 bg-stone-200 rounded-full mb-2">
                        <div 
                          className={`h-full rounded-full ${op.percentagem >= 100 ? 'bg-green-500' : 'bg-primary'}`}
                          style={{ width: `${Math.min(op.percentagem, 100)}%` }}
                        />
                      </div>
                      
                      {/* Info adicional: defeitos, arranjos, tempo */}
                      <div className="flex flex-wrap gap-3 text-xs mb-2">
                        {op.defeitos > 0 && (
                          <span className="text-red-600 bg-red-50 px-2 py-0.5 rounded">
                            {op.defeitos} defeitos
                          </span>
                        )}
                        {op.arranjos > 0 && (
                          <span className="text-amber-600 bg-amber-50 px-2 py-0.5 rounded">
                            {op.arranjos} arranjos
                          </span>
                        )}
                        {op.tempo_total_horas > 0 && (
                          <span className="text-stone-600 bg-stone-100 px-2 py-0.5 rounded">
                            Tempo: {formatTempoHMS(op.tempo_total_horas)}
                          </span>
                        )}
                        <span className="text-stone-500">
                          {op.quantidade_produzida || 0} un. produzidas
                        </span>
                      </div>
                      
                      {op.participantes && op.participantes.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {op.participantes.map((p) => (
                            <span key={p.id} className="text-xs bg-stone-100 px-2 py-0.5 rounded">
                              {p.nome} ({p.quantidade} un. · {formatTempoHMS(p.tempo_horas)})
                            </span>
                          ))}
                        </div>
                      )}
                    </Card>
                  ))}
                </div>
              </div>
              
              {/* Participantes */}
              {acompanharData.participantes && acompanharData.participantes.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-3">Operadores Participantes ({acompanharData.participantes.length})</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {acompanharData.participantes.map((p) => (
                      <Card key={p.id} className="p-3 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          {p.avatar || p.nome?.charAt(0)}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-sm">{p.nome}</p>
                          <p className="text-xs text-stone-500">{p.quantidade_total} un. · {formatTempoHMS(p.tempo_total_horas)}</p>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </GestorLayout>
  );
}
